import { useEffect, useMemo, useRef, useState, useCallback, lazy, Suspense } from "react"
import { useSearchParams } from "react-router-dom"
import { X } from "lucide-react"
import DeckGL from "@deck.gl/react"
import { ScatterplotLayer } from "@deck.gl/layers"
import { Map } from "react-map-gl/maplibre"
import "maplibre-gl/dist/maplibre-gl.css"
import { useTheme } from "@/layout/ThemeProvider"
import chroma from "chroma-js"
import ClusterDetailPanel from "./ClusterDetailPanel"
import MapLegend from "./MapLegend"
import FilterPanel from "./FilterPanel"
import CountryFilterCard from "./CountryFilterCard"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import DateRangeCard from "./DateRangeCard"
import CoverageHistogramCard from "./CoverageHistogramCard"
import MapZoomControls from "./MapZoomControls"
import ValidationFailuresButton from "./ValidationFailuresButton"
import { Database, Settings, BarChart3 } from "lucide-react"
import { useIsMobile } from "@/hooks/use-mobile"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import CoverageHistogramPanel from "./CoverageHistogramPanel"
import { SPECIES_COLUMN_MAP, SPECIES_CONFIG, classifyAgeGroup, type AgeGroup, type CountryOption } from "./FilterList"
import {
  parseUrlState,
  buildSearchParams,
  findPointByKey,
  buildPointKey,
  type ParasiteRateUrlState,
  type MapViewUrlState,
} from "./parasiteRateUrlState"

import { getVersionsForMajor } from "@/services/parasiteRateService"

import { WebMercatorViewport, FlyToInterpolator } from "@deck.gl/core"
import type { MapViewState, PickingInfo, ViewStateChangeParameters } from "@deck.gl/core"
import type { ParasiteRatePoint, ValidationFailurePoint } from "@/services/parasiteRateService"

// Lazy-load the query dialog's heavy SQLRooms/DuckDB-WASM chunk so it stays out
// of the map's critical path. Shared between lazy() and the idle prefetch.
const importPrClusterQueryDialog = () => import("./PrClusterQueryDialog")
const PrClusterQueryDialog = lazy(importPrClusterQueryDialog)

/**
 * Initial view state centered on the world with Africa roughly in focus,
 * since malaria parasite rate data is concentrated in sub-Saharan Africa.
 */
const INITIAL_VIEW_STATE: MapViewState = {
  longitude: 20,
  latitude: 5,
  zoom: 2.5,
  pitch: 0,
  bearing: 0,
}

/** How much the zoom level changes per click of the +/- zoom buttons. */
const ZOOM_STEP = 1

/** Lowest zoom level the +/- buttons will drive the camera to. */
const MIN_ZOOM = 1

/** Highest zoom level the +/- buttons will drive the camera to. */
const MAX_ZOOM = 18

/** Duration (ms) of the animated transition when a zoom button is clicked. */
const ZOOM_TRANSITION_MS = 300

/**
 * CartoDB basemap style URLs (free, no API key required).
 * Dark-matter for dark mode, Positron for light mode.
 */
const MAP_STYLES = {
  dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  light: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
} as const

/**
 * Resolves the effective map style URL based on the app's current theme.
 * When theme is "system", checks the OS-level prefers-color-scheme media query.
 *
 * @param theme - The current theme value from ThemeProvider ("dark" | "light" | "system").
 * @returns The appropriate CartoDB basemap style URL.
 */
function getMapStyle(theme: string): string {
  if (theme === "dark") return MAP_STYLES.dark
  if (theme === "light") return MAP_STYLES.light

  // "system" — resolve from OS preference
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
  return prefersDark ? MAP_STYLES.dark : MAP_STYLES.light
}

/**
 * Chroma color scale for mapping parasite rate values (0–1) to colors.
 **/
const COLOR_SCALE = chroma.scale(["#ffffb2", "#fd8d3c", "#bd0026"]).mode("lab").domain([0, 1])

/**
 * Radius range (in meters) for proportional circles based on the "examined"
 * sample size
 */
const RADIUS_MIN_METERS = 1200
const RADIUS_MAX_METERS = 24000

/**
 * Identifiers for the available functions that map an examined sample size to a
 * circle size.
 */
export type SizeScale = "logarithmic" | "linear" | "sqrt"

/**
 * Option describing a single size-scale choice for the legend dropdown.
 */
export interface SizeScaleOption {
  /** The scale identifier used internally. */
  value: SizeScale
  /** Human-readable label shown in the dropdown. */
  label: string
}

/**
 * All selectable size-scale functions, in the order they appear in the dropdown.
 */
export const SIZE_SCALE_OPTIONS: SizeScaleOption[] = [
  { value: "logarithmic", label: "Logarithmic" },
  { value: "linear", label: "Linear" },
  { value: "sqrt", label: "Square root" },
]

/** The default size-scale function applied when the map first renders. */
export const DEFAULT_SIZE_SCALE: SizeScale = "logarithmic"

/**
 * Normalize an examined value to the [0, 1] range using the chosen scale.
 *
 * The ratio (examined / maxExamined) is the linear baseline; the other scales
 * reshape that ratio:
 *   - logarithmic: emphasises differences among smaller counts (right-skewed data)
 *   - linear: raw proportion, largest counts dominate
 *   - sqrt: gentle boost to smaller counts, milder than logarithmic
 *
 * @param examined - The examined sample size for the data point, or null.
 * @param maxExamined - The maximum examined value across the current dataset.
 * @param scale - Which normalization function to apply.
 * @returns A value in [0, 1]; 0 for missing/non-positive inputs.
 */
function normalizeExamined(examined: number | null, maxExamined: number, scale: SizeScale): number {
  if (examined == null || examined <= 0 || maxExamined <= 0) {
    return 0
  }

  // Clamp the raw proportion to [0, 1] to guard against examined > maxExamined.
  const ratio: number = Math.min(1, examined / maxExamined)

  switch (scale) {
    case "logarithmic":
      return Math.log(examined + 1) / Math.log(maxExamined + 1)
    case "linear":
      return ratio
    case "sqrt":
      return Math.sqrt(ratio)
    default:
      // Fail loudly on an unhandled scale rather than silently defaulting.
      throw new Error(`Unhandled size scale: ${scale as string}`)
  }
}

/**
 * Compute the map radius (in meters) for a data point based on its "examined"
 * value, using the selected size-scale function to normalize the count first.
 *
 * @param examined - The examined sample size for the data point, or null.
 * @param maxExamined - The maximum examined value across the current dataset.
 * @param scale - Which size-scale function to apply.
 * @returns A radius in meters between RADIUS_MIN_METERS and RADIUS_MAX_METERS.
 */
export function examinedToRadius(examined: number | null, maxExamined: number, scale: SizeScale): number {
  const normalized: number = normalizeExamined(examined, maxExamined, scale)
  return RADIUS_MIN_METERS + normalized * (RADIUS_MAX_METERS - RADIUS_MIN_METERS)
}

/**
 * Inverse of {@link normalizeExamined}: given a normalized size fraction in
 * [0, 1], recover the examined count that produces it under the given scale.
 *
 * @param normalized - A size fraction in [0, 1] (0 = smallest, 1 = largest).
 * @param maxExamined - The maximum examined value across the current dataset.
 * @param scale - Which size-scale function is active.
 * @returns The examined count corresponding to that size fraction.
 */
export function normalizedToExamined(normalized: number, maxExamined: number, scale: SizeScale): number {
  if (maxExamined <= 0) {
    return 0
  }

  switch (scale) {
    case "logarithmic":
      // Invert normalized = log(e + 1) / log(maxExamined + 1).
      return Math.pow(maxExamined + 1, normalized) - 1
    case "linear":
      // Invert normalized = e / maxExamined.
      return normalized * maxExamined
    case "sqrt":
      // Invert normalized = sqrt(e / maxExamined).
      return normalized * normalized * maxExamined
    default:
      // Fail loudly on an unhandled scale rather than silently defaulting.
      throw new Error(`Unhandled size scale: ${scale as string}`)
  }
}

/**
 * Map a positive rate value (0–1) to an RGBA color using the shared color scale.
 * Null values render as grey.
 *
 * @param rate - rate value (0–1), or null.
 * @returns An RGBA tuple [r, g, b, a] where each channel is 0–255.
 */
function rateToColor(rate: number | null): [number, number, number, number] {
  if (rate == null) {
    return [128, 128, 128, 160]
  }

  const clamped = Math.max(0, Math.min(1, rate))
  const [r, g, b] = COLOR_SCALE(clamped).rgb()

  return [r, g, b, 200]
}

/**
 * Decimal places used when deciding whether two points share the same map
 * location.
 */
const COLOCATION_COORD_DECIMALS = 5

/**
 * Test whether two points sit at the same map location (same latitude and
 * longitude, each rounded to {@link COLOCATION_COORD_DECIMALS} decimals).
 *
 * Points missing either coordinate are never considered co-located, because an
 * unplottable point has no location to share.
 *
 * @param a - The first point to compare.
 * @param b - The second point to compare.
 * @returns True when both points have coordinates that round to the same
 *   location; false otherwise.
 */
function isSameLocation(a: ParasiteRatePoint, b: ParasiteRatePoint): boolean {
  if (a.latitude == null || a.longitude == null || b.latitude == null || b.longitude == null) {
    return false
  }

  return (
    a.latitude.toFixed(COLOCATION_COORD_DECIMALS) === b.latitude.toFixed(COLOCATION_COORD_DECIMALS) &&
    a.longitude.toFixed(COLOCATION_COORD_DECIMALS) === b.longitude.toFixed(COLOCATION_COORD_DECIMALS)
  )
}

/**
 * A geographic bounding box expressed as two corners in [longitude, latitude]
 * order, matching what deck.gl's {@link WebMercatorViewport.fitBounds} expects.
 */
type LngLatBounds = [[number, number], [number, number]]

/**
 * Compute the geographic bounding box that encloses every plottable point in
 * the given list.
 *
 * Points missing either coordinate are skipped, since they cannot be placed on
 * the map and therefore do not constrain the box.
 *
 * @param points - The points to enclose.
 * @returns The [[minLng, minLat], [maxLng, maxLat]] bounds, or null when none
 *   of the points have usable coordinates.
 */
function computeBounds(points: readonly ParasiteRatePoint[]): LngLatBounds | null {
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity

  for (const point of points) {
    if (point.longitude == null || point.latitude == null) {
      continue
    }
    if (point.longitude < minLng) minLng = point.longitude
    if (point.longitude > maxLng) maxLng = point.longitude
    if (point.latitude < minLat) minLat = point.latitude
    if (point.latitude > maxLat) maxLat = point.latitude
  }

  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) {
    return null
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

/**
 * Padding (in pixels) left around the fitted bounds so points are not flush
 * against the map edge when auto-zooming to a filter's newly-added data.
 */
const FIT_BOUNDS_PADDING_PX = 80

/**
 * Upper bound on the zoom level produced by an auto-fit.
 */
const FIT_BOUNDS_MAX_ZOOM = 7

/**
 * Duration (in milliseconds) of the animated fly-to when the camera auto-fits
 * to newly-added filter data.
 */
const FIT_BOUNDS_TRANSITION_MS = 1000

/** Delimiter separating individual failure reasons within a validation_error string. */
const VALIDATION_ERROR_SEP = ";"

/**
 * Split a cluster's `validation_error` string into its individual failure
 * reasons. A single cluster can fail validation for multiple reasons, which
 * the backend joins into one string delimited by {@link VALIDATION_ERROR_SEP}.
 * Each reason is trimmed, and empty segments (e.g. from a trailing delimiter)
 * are dropped.
 *
 * @param validationError - The raw validation_error string for a cluster, or null.
 * @returns An array of trimmed, non-empty individual failure reasons. Empty
 *   when the input is null, empty, or only whitespace/delimiters.
 */
function splitValidationErrors(validationError: string | null): string[] {
  if (validationError == null) {
    return []
  }
  return validationError
    .split(VALIDATION_ERROR_SEP)
    .map((reason) => reason.trim())
    .filter((reason) => reason.length > 0)
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ParasiteRateMapProps {
  /** Array of parasite rate data points to render on the map. */
  points: ParasiteRatePoint[]
  /** Array of validation failure points to display in error mode. */
  validationFailures: ValidationFailurePoint[]
  /** The currently loaded dataset version string (e.g. "v2.3"). */
  currentVersion: string
  /** All available dataset versions for the version selector dropdown. */
  allVersions: string[]
  /** Callback when the user selects a different version. */
  onVersionChange: (version: string) => void
  /**
   * Resolves an Auth0 access token, used by the country PR coverage histogram
   * shown above the date slider (the map otherwise makes no authenticated
   * requests of its own).
   */
  getAccessToken: () => Promise<string>
}

/**
 * ParasiteRateMap renders a full-viewport deck.gl map of the world using
 * MapLibre GL as the base map provider, with parasite rate data points
 * rendered as a ScatterplotLayer.
 *
 * The basemap adapts to the app's light/dark theme.
 *
 * @param props - Component props containing the data points array.
 * @returns A React element containing the deck.gl + MapLibre map with data.
 */
/** Default color-by column applied when the URL does not specify one. */
const DEFAULT_COLOR_COLUMN = "computed_total_pf_positive_rate"


const DEFAULT_SELECTED_SPECIES: string[] = SPECIES_CONFIG.map((species) => species.label)

export default function ParasiteRateMap({ points, validationFailures, currentVersion, allVersions, onVersionChange, getAccessToken }: ParasiteRateMapProps): React.JSX.Element {
  const { theme } = useTheme()
  const mapStyle = getMapStyle(theme)

  // Restrict the full version list to the UI's supported major version before
  // handing it to the (layout-agnostic) VersionSelector, which renders as-is.
  const selectableVersions: string[] = useMemo(
    (): string[] => getVersionsForMajor({ versions: allVersions }),
    [allVersions],
  )

  // Read the shareable state out of the URL exactly once.
  const [searchParams, setSearchParams] = useSearchParams()
  const initialUrlState: ParasiteRateUrlState = useMemo(
    () => parseUrlState(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const [selectedPoint, setSelectedPoint] = useState<ParasiteRatePoint | null>(null)

  // Below md (768px) the map shows only itself + the legend; the control cards
  // move into a left drawer and PR coverage becomes a bottom-centre popup.
  const isMobile: boolean = useIsMobile()
  const [isControlsDrawerOpen, setIsControlsDrawerOpen] = useState<boolean>(false)
  const [isCoveragePopupOpen, setIsCoveragePopupOpen] = useState<boolean>(false)
  const [colorColumn, setColorColumn] = useState<string>(initialUrlState.colorColumn ?? DEFAULT_COLOR_COLUMN)
  const [sizeScale, setSizeScale] = useState<SizeScale>((initialUrlState.sizeScale as SizeScale) ?? DEFAULT_SIZE_SCALE)
  const [selectedSpecies, setSelectedSpecies] = useState<string[]>(initialUrlState.selectedSpecies ?? DEFAULT_SELECTED_SPECIES)
  const [selectedMethods, setSelectedMethods] = useState<string[]>(initialUrlState.selectedMethods ?? [])
  const [selectedCountries, setSelectedCountries] = useState<string[]>(initialUrlState.selectedCountries ?? [])
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>(initialUrlState.selectedSourceIds ?? [])
  const [selectedMapSurveyIds, setSelectedMapSurveyIds] = useState<string[]>(initialUrlState.selectedMapSurveyIds ?? [])
  const [onlySurveyData, setOnlySurveyData] = useState<boolean>(initialUrlState.onlySurveyData ?? false)
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<AgeGroup[]>(initialUrlState.selectedAgeGroups ?? [])
  const [errorModeActive, setErrorModeActive] = useState<boolean>(initialUrlState.errorModeActive ?? false)
  const [selectedValidationErrors, setSelectedValidationErrors] = useState<string[]>([])

  // SQL query dialog open state. Owned here so the trigger button is always
  // present; the lazy dialog body mounts only after the first open.
  const [queryDialogOpen, setQueryDialogOpen] = useState<boolean>(false)
  const [queryDialogMounted, setQueryDialogMounted] = useState<boolean>(false)

  // Prefetch the dialog chunk when idle so the first open is instant.
  useEffect(() => {
    const hasIdleCallback: boolean = "requestIdleCallback" in window
    const handle: number = hasIdleCallback
      ? window.requestIdleCallback(() => void importPrClusterQueryDialog())
      : window.setTimeout(() => void importPrClusterQueryDialog(), 2000)
    return () => {
      if (hasIdleCallback) {
        window.cancelIdleCallback(handle)
      } else {
        window.clearTimeout(handle)
      }
    }
  }, [])

  // Controlled map view (bounding box). Seeded from the URL when a shared link
  // specifies one, otherwise from the default world-centered view.
  const [viewState, setViewState] = useState<MapViewState>(() =>
    initialUrlState.view != null
      ? { ...INITIAL_VIEW_STATE, ...initialUrlState.view }
      : INITIAL_VIEW_STATE,
  )
  const latestViewRef = useRef<MapViewUrlState>({
    longitude: viewState.longitude,
    latitude: viewState.latitude,
    zoom: viewState.zoom,
    pitch: viewState.pitch ?? 0,
    bearing: viewState.bearing ?? 0,
  })

  /**
   * Handle deck.gl view state changes (pan / zoom / rotate). Updates the
   * controlled view state and records the latest view for the URL sync.
   *
   * @param params - deck.gl callback payload; `viewState` holds the new camera.
   */
  const onViewStateChange = useCallback((params: ViewStateChangeParameters): void => {
    const next = params.viewState as MapViewState
    setViewState(next)
    latestViewRef.current = {
      longitude: next.longitude,
      latitude: next.latitude,
      zoom: next.zoom,
      pitch: next.pitch ?? 0,
      bearing: next.bearing ?? 0,
    }
  }, [])

  const zoomBySteps = useCallback((steps: number): void => {
    const current: MapViewUrlState = latestViewRef.current
    // Clamp so repeated clicks saturate at the bounds instead of overshooting.
    const nextZoom: number = Math.max(
      MIN_ZOOM,
      Math.min(MAX_ZOOM, current.zoom + steps * ZOOM_STEP),
    )

    const nextView: MapViewState = {
      longitude: current.longitude,
      latitude: current.latitude,
      zoom: nextZoom,
      pitch: current.pitch,
      bearing: current.bearing,
      transitionDuration: ZOOM_TRANSITION_MS,
      transitionInterpolator: new FlyToInterpolator(),
    }

    setViewState(nextView)
    latestViewRef.current = {
      longitude: current.longitude,
      latitude: current.latitude,
      zoom: nextZoom,
      pitch: current.pitch,
      bearing: current.bearing,
    }
  }, [])

  /** Zoom the camera in by one step (bound to the "+" control). */
  const handleZoomIn = useCallback((): void => {
    zoomBySteps(1)
  }, [zoomBySteps])

  /** Zoom the camera out by one step (bound to the "−" control). */
  const handleZoomOut = useCallback((): void => {
    zoomBySteps(-1)
  }, [zoomBySteps])

  /**
   * Reset the camera to the default world view (the "100%" control). Animates
   * back to INITIAL_VIEW_STATE and keeps the latest-view ref in sync so the URL
   * reflects the reset view.
   */
  const handleResetView = useCallback((): void => {
    const nextView: MapViewState = {
      ...INITIAL_VIEW_STATE,
      transitionDuration: ZOOM_TRANSITION_MS,
      transitionInterpolator: new FlyToInterpolator(),
    }

    setViewState(nextView)
    latestViewRef.current = {
      longitude: INITIAL_VIEW_STATE.longitude,
      latitude: INITIAL_VIEW_STATE.latitude,
      zoom: INITIAL_VIEW_STATE.zoom,
      pitch: INITIAL_VIEW_STATE.pitch ?? 0,
      bearing: INITIAL_VIEW_STATE.bearing ?? 0,
    }
  }, [])

  /**
   * Derive unique method values from the dataset for the method filter options.
   */
  const methodOptions = useMemo(() => {
    const methods = new Set<string>()
    for (const point of points) {
      if (point.method != null && point.method !== "") {
        methods.add(point.method)
      }
    }
    return Array.from(methods).sort()
  }, [points])

  /**
   * Derive the country filter options from the dataset rows.
   *
   * Each point carries a stable ISO3 code (`country_id`, e.g. "AFG") and a
   * display name (`country`, e.g. "Afghanistan"). The ISO3 is the identity used
   * for selection, URL state, and matching; the name is only for display. We
   * build one option per distinct ISO3 (first name seen wins) and sort by name.
   */
  const countryOptions = useMemo((): CountryOption[] => {
    // iso3 -> display name, deduplicated across rows.
    const iso3ToName: Record<string, string> = {}
    for (const point of points) {
      const iso3 = point.country_id
      const name = point.country
      if (iso3 == null || iso3 === "" || name == null || name === "") {
        continue
      }
      if (!(iso3 in iso3ToName)) {
        iso3ToName[iso3] = name
      }
    }

    return Object.entries(iso3ToName)
      .map(([iso3, name]): CountryOption => ({ iso3, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [points])

  /**
   * The country the PR coverage card shows right now, from either a clicked
   * point or the single-select country filter, or null when neither is set.
   * This can briefly go null mid-interaction — e.g. dragging the date slider
   * can filter the selected point out of the set, which clears selectedPoint —
   * so it is not used directly by the card; coverageCountry below smooths it.
   */
  const liveCoverageCountry = useMemo((): CountryOption | null => {
    const pointCountryId: string | null = selectedPoint?.country_id ?? null
    if (pointCountryId != null && pointCountryId !== "") {
      return {
        iso3: pointCountryId,
        name: selectedPoint?.country ?? pointCountryId,
      }
    }

    const filterCountryId: string | null = selectedCountries[0] ?? null
    if (filterCountryId != null && filterCountryId !== "") {
      const option: CountryOption | undefined = countryOptions.find(
        (candidate) => candidate.iso3 === filterCountryId,
      )
      return { iso3: filterCountryId, name: option?.name ?? filterCountryId }
    }

    return null
  }, [selectedPoint, selectedCountries, countryOptions])

  // The country the coverage card actually displays. It latches onto the last
  // live country and only clears on an explicit user deselection (see
  // clearCoverageCountry), so the card survives the selected point being
  // filtered out of the set by the date range — a view change, not a
  // deselection.
  const [coverageCountry, setCoverageCountry] = useState<CountryOption | null>(null)

  // Adopt each new live country, but never auto-clear: a null live value is
  // ambiguous (could be a deselection or just a point filtered out by the date
  // range), so clearing is driven explicitly from the deselect handlers.
  useEffect(() => {
    if (liveCoverageCountry != null) {
      setCoverageCountry(liveCoverageCountry)
    }
  }, [liveCoverageCountry])

  /**
   * Clear the PR coverage card. Called from genuine user deselections (clicking
   * the map background, toggling the selected point off, closing the detail
   * panel, or clearing the country filter) — never from filter-driven point
   * removal, which must leave the card in place.
   */
  const clearCoverageCountry = useCallback((): void => {
    setCoverageCountry(null)
  }, [])

  /**
   * Derive unique source_id1 values from the dataset, sorted alphanumerically.
   */
  const sourceIdOptions = useMemo(() => {
    const ids = new Set<string>()
    for (const point of points) {
      if (point.source_id1 != null && point.source_id1 !== "") {
        ids.add(point.source_id1)
      }
    }
    return Array.from(ids).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }, [points])

  /**
   * Derive unique map_survey_id values from the dataset, sorted alphanumerically.
   */
  const mapSurveyIdOptions = useMemo(() => {
    const ids = new Set<string>()
    for (const point of points) {
      if (point.map_survey_id != null && point.map_survey_id !== "") {
        ids.add(point.map_survey_id)
      }
    }
    return Array.from(ids).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }, [points])

  const validationErrorOptions = useMemo(() => {
    const errors = new Set<string>()
    for (const failure of validationFailures) {
      // Each cluster's validation_error holds one or more reasons joined by
      // ";". Split so each individual reason becomes its own filter option.
      for (const reason of splitValidationErrors(failure.validation_error)) {
        errors.add(reason)
      }
    }
    return Array.from(errors).sort((a, b) => a.localeCompare(b))
  }, [validationFailures])

  /**
   * Derive min and max date values from the dataset as year decimals.
   * Min = earliest year_start + month_start, Max = latest year_end + month_end.
   */
  const { minDate, maxDate } = useMemo(() => {
    let min = Infinity
    let max = -Infinity
    for (const point of points) {
      if (point.year_start != null) {
        const startDecimal = point.year_start + ((point.month_start ?? 1) - 1) / 12
        if (startDecimal < min) min = startDecimal
      }
      if (point.year_end != null) {
        const endDecimal = point.year_end + ((point.month_end ?? 12) - 1) / 12
        if (endDecimal > max) max = endDecimal
      }
    }
    // Fallback if no valid dates
    if (min === Infinity) min = 1900
    if (max === -Infinity) max = new Date().getFullYear()
    return { minDate: min, maxDate: max }
  }, [points])

  const [dateRangeStart, setDateRangeStart] = useState<number | null>(initialUrlState.dateRangeStart ?? null)
  const [dateRangeEnd, setDateRangeEnd] = useState<number | null>(initialUrlState.dateRangeEnd ?? null)

  // Default start of the date range when the user hasn't set one: January 2000
  // (year decimal 2000.0), matching the PR coverage histogram's start year. The
  // pre-2000 range is not the focus, so the slider opens filtered to 2000
  // onward rather than the dataset's earliest date. It is clamped into
  // [minDate, maxDate] so a dataset that starts after 2000 (or ends before it)
  // still gets a valid default.
  const DEFAULT_RANGE_START_YEAR: number = 2000
  const defaultDateStart: number = Math.min(
    Math.max(DEFAULT_RANGE_START_YEAR, minDate),
    maxDate,
  )

  // Effective range values: fall back to the defaults until the user interacts.
  const effectiveDateStart = dateRangeStart ?? defaultDateStart
  const effectiveDateEnd = dateRangeEnd ?? maxDate

  // Debounced copies of the effective range, used only to drive the PR coverage
  // histogram's dimming. Dragging the slider fires continuously; recomputing
  // the dimmed-year set and re-rendering the chart on every tick is wasteful
  // and makes the drag feel laggy, so the histogram settles ~150ms after the
  // drag pauses. The slider and map filtering still update live from the
  // undebounced values.
  const debouncedCoverageRangeStart: number = useDebouncedValue({
    value: effectiveDateStart,
    delayMs: 150,
  })
  const debouncedCoverageRangeEnd: number = useDebouncedValue({
    value: effectiveDateEnd,
    delayMs: 150,
  })

  function resetFilters(): void {
    setSelectedSpecies([...DEFAULT_SELECTED_SPECIES])
    setSelectedMethods([])
    setSelectedSourceIds([])
    setSelectedMapSurveyIds([])
    setSelectedAgeGroups([])
    setSelectedValidationErrors([])
    setOnlySurveyData(false)
  }

  /**
   * Shared predicate that applies the active map filters to a single point.
   * Used by both the main data and validation failure filtering logic.
   *
   * Checks: species, method, source ID, data origin (survey-only), and
   * date range overlap.
   *
   * @param point - A parasite rate data point to test.
   * @returns True if the point passes all active filters.
   */
  const passesFilters = useCallback((point: ParasiteRatePoint): boolean => {
    // Species filter: a point must have a non-null value in at least one of the
    // selected species columns. When no species are selected, no point can pass
    // this filter, so nothing is shown on the map.
    if (selectedSpecies.length === 0) {
      return false
    }
    const passesSpecies: boolean = selectedSpecies.some((species) => {
      const col = SPECIES_COLUMN_MAP[species]
      return col != null && point[col] != null
    })
    if (!passesSpecies) {
      return false
    }

    if (selectedMethods.length > 0) {
      if (point.method == null || !selectedMethods.includes(point.method)) {
        return false
      }
    }

    // Country filter (only applies if the user has selected specific
    // countries). selectedCountries holds ISO3 codes, so match directly on the
    // point's country_id (the ISO3 code).
    if (selectedCountries.length > 0) {
      if (point.country_id == null || !selectedCountries.includes(point.country_id)) {
        return false
      }
    }

    // Source ID filter (only applies if user has selected specific IDs)
    if (selectedSourceIds.length > 0) {
      if (point.source_id1 == null || !selectedSourceIds.includes(point.source_id1)) {
        return false
      }
    }

    // MAP Survey ID filter (only applies if user has selected specific IDs)
    if (selectedMapSurveyIds.length > 0) {
      if (point.map_survey_id == null || !selectedMapSurveyIds.includes(point.map_survey_id)) {
        return false
      }
    }

    // Data Origin filter: when toggle is on, only show points with a map_survey_id
    if (onlySurveyData) {
      if (point.map_survey_id == null) {
        return false
      }
    }

    if (selectedAgeGroups.length > 0) {
      const ageGroup = classifyAgeGroup(point.lower_age_months_rounded, point.upper_age_months_rounded)
      if (!selectedAgeGroups.includes(ageGroup)) {
        return false
      }
    }

    // Date range filter: point's survey period must overlap with selected range
    if (point.year_start != null && point.year_end != null) {
      const pointStart = point.year_start + ((point.month_start ?? 1) - 1) / 12
      const pointEnd = point.year_end + ((point.month_end ?? 12) - 1) / 12
      if (pointEnd < effectiveDateStart || pointStart > effectiveDateEnd) {
        return false
      }
    }

    return true
  }, [selectedSpecies, selectedMethods, selectedCountries, selectedSourceIds, selectedMapSurveyIds, onlySurveyData, selectedAgeGroups, effectiveDateStart, effectiveDateEnd])

  /**
   * Filter main data points using the shared filter predicate.
   */
  const filteredPoints = useMemo(() => {
    return points.filter(passesFilters)
  }, [points, passesFilters])

  /**
   * Selected survey IDs that do not exist anywhere in the loaded dataset. These
   * are surveys the user filtered to (typically by following a survey-catalogue
   * link) that simply have no parasite rate points.
   */
  const noDataSurveyIds: string[] = useMemo(() => {
    if (selectedMapSurveyIds.length === 0 || mapSurveyIdOptions.length === 0) {
      return []
    }
    const availableIds: Set<string> = new Set(mapSurveyIdOptions)
    return selectedMapSurveyIds.filter((id) => !availableIds.has(id))
  }, [selectedMapSurveyIds, mapSurveyIdOptions])

  /**
   * Clear the no-data survey IDs from the active survey filter. Removes only the
   * survey IDs that have no data (leaving any valid selected surveys in place),
   * which drops the phantom filter and its URL param and hides the notice.
   */
  const clearNoDataSurveyFilter = useCallback((): void => {
    const noDataSet: Set<string> = new Set(noDataSurveyIds)
    setSelectedMapSurveyIds((prev) => prev.filter((id) => !noDataSet.has(id)))
  }, [noDataSurveyIds])

  /**
   * Filter validation failure points using the same shared filter predicate,
   * then additionally narrow by the validation-error selection.
   *
   * @returns The validation-failure points that pass both the shared filters
   *   and the active validation-error selection.
   */
  const filteredValidationFailures = useMemo(() => {
    const passingSharedFilters: ValidationFailurePoint[] = validationFailures.filter(passesFilters)

    if (selectedValidationErrors.length === 0) {
      return passingSharedFilters
    }

    const selectedErrorSet: Set<string> = new Set(selectedValidationErrors)
    return passingSharedFilters.filter((failure) =>
      splitValidationErrors(failure.validation_error).some((reason) => selectedErrorSet.has(reason)),
    )
  }, [validationFailures, passesFilters, selectedValidationErrors])

  // Point key from the shared URL, pending until the data loads. Cleared once resolved.
  const pendingPointKeyRef = useRef<string | null>(initialUrlState.pointKey ?? null)

  // Wrapper element around the deck.gl canvas.
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Fly the camera so the given points all fit within the current viewport.
  const flyToPoints = useCallback((pointsToFit: readonly ParasiteRatePoint[]): void => {
    const container: HTMLDivElement | null = containerRef.current
    if (container == null) {
      return
    }

    const bounds: LngLatBounds | null = computeBounds(pointsToFit)
    if (bounds == null) {
      return
    }

    const width: number = container.clientWidth
    const height: number = container.clientHeight
    if (width === 0 || height === 0) {
      return
    }

    // Use a throwaway viewport sized to the current container to translate the
    // bounds into a center + zoom, then clamp the zoom to the max.
    const viewport = new WebMercatorViewport({ width, height })
    const { longitude, latitude, zoom } = viewport.fitBounds(bounds, {
      padding: FIT_BOUNDS_PADDING_PX,
    })

    // Preserve the current pitch/bearing (read from the ref so this callback
    // does not need to depend on, and be recreated by, every viewState frame).
    const cappedZoom: number = Math.min(zoom, FIT_BOUNDS_MAX_ZOOM)
    const nextView: MapViewState = {
      longitude,
      latitude,
      zoom: cappedZoom,
      pitch: latestViewRef.current.pitch,
      bearing: latestViewRef.current.bearing,
      transitionDuration: FIT_BOUNDS_TRANSITION_MS,
      transitionInterpolator: new FlyToInterpolator(),
    }

    setViewState(nextView)
    latestViewRef.current = {
      longitude,
      latitude,
      zoom: cappedZoom,
      pitch: latestViewRef.current.pitch,
      bearing: latestViewRef.current.bearing,
    }
  }, [])

  // Previous values of the three "location" filters, used to detect which
  // specific values were just added.
  const prevLocationFiltersRef = useRef<{
    countries: string[]
    sourceIds: string[]
    mapSurveyIds: string[]
  }>(
    initialUrlState.view != null
      ? {
          countries: selectedCountries,
          sourceIds: selectedSourceIds,
          mapSurveyIds: selectedMapSurveyIds,
        }
      : { countries: [], sourceIds: [], mapSurveyIds: [] },
  )

  // Auto-zoom to the data a location filter just added.
  useEffect(() => {
    const prev = prevLocationFiltersRef.current

    const addedCountries: string[] = selectedCountries.filter((c) => !prev.countries.includes(c))
    const addedSourceIds: string[] = selectedSourceIds.filter((s) => !prev.sourceIds.includes(s))
    const addedMapSurveyIds: string[] = selectedMapSurveyIds.filter((s) => !prev.mapSurveyIds.includes(s))

    const hasAddition: boolean =
      addedCountries.length > 0 || addedSourceIds.length > 0 || addedMapSurveyIds.length > 0

    if (hasAddition) {
      const addedCountrySet: Set<string> = new Set(addedCountries)
      const addedSourceSet: Set<string> = new Set(addedSourceIds)
      const addedSurveySet: Set<string> = new Set(addedMapSurveyIds)
      
      const addedPoints: ParasiteRatePoint[] = points.filter((p) => {
        const matchesCountry: boolean = p.country_id != null && addedCountrySet.has(p.country_id)
        const matchesSource: boolean = p.source_id1 != null && addedSourceSet.has(p.source_id1)
        const matchesSurvey: boolean = p.map_survey_id != null && addedSurveySet.has(p.map_survey_id)
        return matchesCountry || matchesSource || matchesSurvey
      })

      if (addedPoints.length > 0) {
        flyToPoints(addedPoints)
      }
    }

    // Advance the snapshot so the next change is diffed against the current
    // state, regardless of whether the camera moved this time.
    prevLocationFiltersRef.current = {
      countries: selectedCountries,
      sourceIds: selectedSourceIds,
      mapSurveyIds: selectedMapSurveyIds,
    }
  }, [selectedCountries, selectedSourceIds, selectedMapSurveyIds, points, flyToPoints])

  // Keep the selected point matched to the active filtered set by key (not
  // reference, which breaks when the data reloads with fresh objects).
  useEffect(() => {
    const activeSet: ParasiteRatePoint[] = errorModeActive ? filteredValidationFailures : filteredPoints

    const pendingKey: string | null = pendingPointKeyRef.current
    if (pendingKey != null) {
      const match: ParasiteRatePoint | null = findPointByKey(activeSet, pendingKey)
      if (match != null) {
        setSelectedPoint(match)
        pendingPointKeyRef.current = null
      }
      return
    }

    if (selectedPoint == null) return

    // Nothing to do while the exact selected object is still present
    if (activeSet.includes(selectedPoint)) {
      return
    }

    // The selected object is gone (data reloaded with fresh instances, or dev
    // Strict Mode made the ref stale)
    setSelectedPoint(findPointByKey(activeSet, buildPointKey(selectedPoint)))
  }, [filteredPoints, filteredValidationFailures, selectedPoint, errorModeActive])

  // Single writer that mirrors the full shareable state (filters, selected
  // point, warning mode, version, and the current map view) into the URL query
  // string. Uses replace (not push) so it does not spam browser history with an
  // entry per filter tweak or pan frame. The view is read from the ref so this
  // always writes the latest bounding box regardless of which change triggered it.
  const writeUrl = useCallback((): void => {
    const nextParams: URLSearchParams = buildSearchParams({
      version: currentVersion,
      colorColumn,
      sizeScale,
      selectedSpecies,
      selectedMethods,
      selectedCountries,
      selectedSourceIds,
      selectedMapSurveyIds,
      onlySurveyData,
      selectedAgeGroups,
      dateRangeStart,
      dateRangeEnd,
      errorModeActive,
      selectedPointKey: selectedPoint != null ? buildPointKey(selectedPoint) : pendingPointKeyRef.current,
      view: latestViewRef.current,
    })
    setSearchParams(nextParams, { replace: true })
  }, [
    currentVersion,
    colorColumn,
    sizeScale,
    selectedSpecies,
    selectedMethods,
    selectedCountries,
    selectedSourceIds,
    selectedMapSurveyIds,
    onlySurveyData,
    selectedAgeGroups,
    dateRangeStart,
    dateRangeEnd,
    errorModeActive,
    selectedPoint,
    setSearchParams,
  ])

  // Write immediately when any discrete (non-view) piece of state changes.
  useEffect(() => {
    writeUrl()
  }, [writeUrl])

  // Write the view (bounding box) on a debounce. Panning and zooming fire
  // onViewStateChange on every animation frame, so we wait for the gesture to
  // settle (250ms) before updating the URL to avoid excessive history writes.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      writeUrl()
    }, 250)
    return () => window.clearTimeout(timeoutId)
  }, [viewState, writeUrl])

  /**
   * Handle click events on the deck.gl canvas. If a point was picked,
   * select it. If the background was clicked, deselect.
   */
  const onClick = useCallback(
    (info: PickingInfo) => {
      if (info.object) {
        const clicked = info.object as ParasiteRatePoint
        setSelectedPoint((prev) => {
          // Clicking the already-selected point toggles it off, which is an
          // explicit deselection and should also close the coverage card.
          if (prev === clicked) {
            clearCoverageCountry()
            return null
          }
          return clicked
        })
      } else {
        // Background click deselects everything.
        setSelectedPoint(null)
        clearCoverageCountry()
      }
    },
    [clearCoverageCountry],
  )

  /**
   * Resolve the CSS cursor for the deck.gl canvas. deck.gl calls this on every
   * pointer move with the current interaction state, so returning "pointer"
   * while hovering a pickable dot makes the cursor change to a hand over the
   * clickable points, while preserving the usual grab / grabbing cursors for
   * panning the map elsewhere.
   *
   * @param state - deck.gl interaction state for the current pointer position.
   * @param state.isHovering - True when the pointer is over a pickable object (a dot).
   * @param state.isDragging - True while the user is actively dragging to pan.
   * @returns The CSS cursor keyword to apply to the canvas.
   */
  const getCursor = useCallback(
    ({ isHovering, isDragging }: { isHovering: boolean; isDragging: boolean }): string => {
      if (isHovering) {
        return "pointer"
      }
      return isDragging ? "grabbing" : "grab"
    },
    [],
  )

  /**
   * All points in the currently active filtered set that sit at the same map
   * location as the selected point (including the selected point itself).
   *
   * Empty when nothing is selected.
   */
  const coLocatedPoints: ParasiteRatePoint[] = useMemo(() => {
    if (selectedPoint == null) {
      return []
    }

    const activeSet: ParasiteRatePoint[] = errorModeActive ? filteredValidationFailures : filteredPoints
    return activeSet.filter((candidate) => isSameLocation(candidate, selectedPoint))
  }, [selectedPoint, errorModeActive, filteredPoints, filteredValidationFailures])

  /**
   * Index of the selected point within {@link coLocatedPoints}. -1 when there
   * is no selection or the selected point is not present in the active set
   */
  const coLocatedIndex: number = useMemo(() => {
    if (selectedPoint == null) {
      return -1
    }
    return coLocatedPoints.indexOf(selectedPoint)
  }, [coLocatedPoints, selectedPoint])

  /**
   * Select another point in the co-located group by stepping the current index
   * and wrapping around at either end. No-op when there is nothing to page
   * through (fewer than two co-located points) or the current index is unknown.
   *
   * @param delta - Direction to move: +1 for next, -1 for previous.
   */
  const navigateCoLocated = useCallback((delta: number): void => {
    if (coLocatedPoints.length < 2 || coLocatedIndex < 0) {
      return
    }

    // Add length before the modulo so a -1 step from index 0 wraps to the end
    // rather than producing a negative index.
    const nextIndex: number = (coLocatedIndex + delta + coLocatedPoints.length) % coLocatedPoints.length
    setSelectedPoint(coLocatedPoints[nextIndex])
  }, [coLocatedPoints, coLocatedIndex])

  /**
   * Compute the maximum "examined" value across the full dataset (not filtered).
   * This keeps the radius scale stable regardless of active filters — a point
   * always renders at the same size no matter what else is visible.
   */
  const maxExamined = useMemo(() => {
    let max = 0
    for (const point of points) {
      if (point.examined != null && point.examined > max) {
        max = point.examined
      }
    }
    return max
  }, [points])

  /**
   * Build deck.gl layers. When error mode is active, renders validation
   * failure points as red-outlined circles. Otherwise renders normal points
   * with radius proportional to the examined sample size, plus a blue outline
   * ring on the selected point.
   */
  const layers = useMemo(() => {
    // Error mode: show filtered validation failure points as red outlined circles
    if (errorModeActive) {
      const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)
      const selectedStrokeColor: [number, number, number, number] = isDark ? [255, 255, 255, 255] : [0, 0, 0, 255]
      const defaultStrokeColor: [number, number, number, number] = [220, 38, 38, 220]

      return [
        new ScatterplotLayer<ParasiteRatePoint>({
          id: "parasite-rate-validation-failures",
          data: filteredValidationFailures,
          getPosition: (d: ParasiteRatePoint) => [d.longitude!, d.latitude!],
          getFillColor: [220, 38, 38, 40],
          getLineColor: (d: ParasiteRatePoint) => d === selectedPoint ? selectedStrokeColor : defaultStrokeColor,
          stroked: true,
          filled: true,
          getRadius: (d: ParasiteRatePoint) => examinedToRadius(d.examined, maxExamined, sizeScale),
          radiusMinPixels: 3,
          radiusMaxPixels: 40,
          getLineWidth: 2,
          lineWidthMinPixels: 2,
          opacity: 0.9,
          pickable: true,
          updateTriggers: {
            getRadius: [maxExamined, sizeScale],
            getLineColor: [selectedPoint, theme],
          },
        }),
      ]
    }

    // Normal mode: standard color-scaled points
    const result = [
      new ScatterplotLayer<ParasiteRatePoint>({
        id: "parasite-rate-points",
        data: filteredPoints,
        getPosition: (d: ParasiteRatePoint) => [d.longitude!, d.latitude!],
        getFillColor: (d: ParasiteRatePoint) => rateToColor(d[colorColumn as keyof ParasiteRatePoint] as number | null),
        stroked: false,
        getRadius: (d: ParasiteRatePoint) => examinedToRadius(d.examined, maxExamined, sizeScale),
        radiusMinPixels: 2,
        radiusMaxPixels: 40,
        opacity: 0.8,
        pickable: true,
        updateTriggers: {
          getFillColor: [colorColumn],
          getRadius: [maxExamined, sizeScale],
        },
      }),
    ]

    if (selectedPoint) {
      result.push(
        new ScatterplotLayer<ParasiteRatePoint>({
          id: "parasite-rate-selected",
          data: [selectedPoint],
          getPosition: (d: ParasiteRatePoint) => [d.longitude!, d.latitude!],
          getFillColor: [0, 0, 0, 0],
          getLineColor: [59, 130, 246, 255],
          stroked: true,
          filled: false,
          getRadius: (d: ParasiteRatePoint) => examinedToRadius(d.examined, maxExamined, sizeScale) * 1.15,
          radiusMinPixels: 6,
          radiusMaxPixels: 44,
          getLineWidth: 3,
          lineWidthMinPixels: 3,
          pickable: false,
        }),
      )
    }

    return result
  }, [filteredPoints, filteredValidationFailures, selectedPoint, colorColumn, maxExamined, sizeScale, errorModeActive, theme])

  return (
    <div ref={containerRef} className="parasite-rate-map relative w-full flex-1 min-h-0 overflow-hidden">
      <DeckGL
        viewState={viewState}
        onViewStateChange={onViewStateChange}
        controller={true}
        layers={layers}
        onClick={onClick}
        getCursor={getCursor}
        style={{ position: "absolute", inset: "0" }}
      >
        <Map
          mapStyle={mapStyle}
          preserveDrawingBuffer={true} //needed to ensure it will be visible in screenshots for feedback
        />
      </DeckGL>

      {/*
        Shown when the active survey filter includes one or more survey IDs that
        have no parasite rate data in the dataset
      */}
      {noDataSurveyIds.length > 0 && (
        <div
          role="status"
          aria-live="polite"
          className="absolute top-24 left-1/2 -translate-x-1/2 z-20 flex items-start gap-3 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-4 py-3 max-w-md"
        >
          <div className="text-sm">
            <p className="font-medium">No parasite rate data for this survey</p>
            <p className="text-muted-foreground">
              {noDataSurveyIds.length === 1
                ? `Survey ${noDataSurveyIds[0]} has no matching points in this dataset.`
                : `These surveys have no matching points in this dataset: ${noDataSurveyIds.join(", ")}.`}
            </p>
          </div>
          <button
            type="button"
            onClick={clearNoDataSurveyFilter}
            aria-label="Clear survey filter"
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      {selectedPoint && (
        <ClusterDetailPanel
          point={selectedPoint}
          onClose={() => {
            setSelectedPoint(null)
            clearCoverageCountry()
          }}
          errorModeActive={errorModeActive}
          coLocatedCount={coLocatedPoints.length}
          coLocatedIndex={coLocatedIndex}
          onNavigatePrev={() => navigateCoLocated(-1)}
          onNavigateNext={() => navigateCoLocated(1)}
          allValidationFailures={validationFailures}
        />
      )}

      {/* The control cards. Shared between desktop (floating overlays) and
          mobile (stacked inside the left drawer) by rendering them here as
          elements. Country+version+metric, the validation/query/filters group,
          and the date range. */}
      {(() => {
        const datasetSelectors = (
          <CountryFilterCard
            countryOptions={countryOptions}
            selectedCountries={selectedCountries}
            onCountriesChange={(nextCountryIso3s) => {
              setSelectedCountries(nextCountryIso3s)
              // Clearing the country filter with no point selected is an explicit
              // deselection, so close the coverage card too.
              if (nextCountryIso3s.length === 0 && selectedPoint == null) {
                clearCoverageCountry()
              }
            }}
            currentVersion={currentVersion}
            selectableVersions={selectableVersions}
            onVersionChange={onVersionChange}
            selectedColumn={colorColumn}
            onColumnChange={setColorColumn}
          />
        )

        const queryButton = (
          <button
            type="button"
            title="Run SQL queries over the parasite rate cluster data"
            onClick={() => {
              setQueryDialogMounted(true)
              setQueryDialogOpen(true)
            }}
            onMouseEnter={() => void importPrClusterQueryDialog()}
            onFocus={() => void importPrClusterQueryDialog()}
            className={[
              "flex w-full items-center justify-start gap-2",
              "rounded-xl border border-border/60 px-4 py-2 shadow-xl ring-1 ring-black/5",
              "bg-background/80 hover:bg-muted/40",
              "text-[11px] font-semibold uppercase tracking-wider text-foreground/80",
              "backdrop-blur-md transition-colors",
            ].join(" ")}
          >
            <Database className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            Query data (SQL)
          </button>
        )

        const validationButton = (
          <ValidationFailuresButton
            errorModeActive={errorModeActive}
            onToggle={setErrorModeActive}
            variant="toggle"
          />
        )

        const filterPanel = (
          <FilterPanel
            errorModeActive={errorModeActive}
            onResetFilters={resetFilters}
            selectedSpecies={selectedSpecies}
            onSpeciesChange={setSelectedSpecies}
            methodOptions={methodOptions}
            selectedMethods={selectedMethods}
            onMethodsChange={setSelectedMethods}
            sourceIdOptions={sourceIdOptions}
            selectedSourceIds={selectedSourceIds}
            onSourceIdsChange={setSelectedSourceIds}
            mapSurveyIdOptions={mapSurveyIdOptions}
            selectedMapSurveyIds={selectedMapSurveyIds}
            onMapSurveyIdsChange={setSelectedMapSurveyIds}
            onlySurveyData={onlySurveyData}
            onSurveyDataToggle={setOnlySurveyData}
            selectedAgeGroups={selectedAgeGroups}
            onAgeGroupsChange={setSelectedAgeGroups}
            validationErrorOptions={validationErrorOptions}
            selectedValidationErrors={selectedValidationErrors}
            onValidationErrorsChange={setSelectedValidationErrors}
          />
        )

        const dateRange = (
          <DateRangeCard
            minDate={minDate}
            maxDate={maxDate}
            rangeStart={effectiveDateStart}
            rangeEnd={effectiveDateEnd}
            onRangeStartChange={setDateRangeStart}
            onRangeEndChange={setDateRangeEnd}
            alignPivotToPx={!isMobile}
          />
        )

        if (isMobile) {
          return (
            <Sheet open={isControlsDrawerOpen} onOpenChange={setIsControlsDrawerOpen}>
              <SheetTrigger
                aria-label="Open map controls"
                className="absolute top-4 left-4 z-20 flex items-center justify-center rounded-xl border border-border/60 bg-background/80 p-2 shadow-xl ring-1 ring-black/5 backdrop-blur-md transition-colors hover:bg-muted/40"
              >
                <Settings className="size-4 text-foreground/80" aria-hidden="true" />
              </SheetTrigger>
              <SheetContent side="left" className="w-80 max-w-[85vw] gap-0 overflow-y-auto p-0">
                <SheetHeader className="border-b border-border/60 px-4 py-3">
                  <SheetTitle className="text-sm">Map controls</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-3 p-4">
                  {datasetSelectors}
                  {dateRange}
                  {filterPanel}
                  {queryButton}
                </div>
              </SheetContent>
            </Sheet>
          )
        }

        // Desktop: the original floating overlay layout.
        return (
          <>
            <div className="absolute top-4 left-1/2 z-20 hidden w-72 max-w-[calc(100%-2rem)] -translate-x-1/2 lg:block min-[1440px]:w-[42rem]">
              {datasetSelectors}
            </div>

            <div className="absolute top-4 left-4 z-10 flex w-49 flex-col gap-2">
              {/* md–lg: dataset selectors stack here above validation to save
                  horizontal space; lg+ shows the top-centre copy instead. */}
              <div className="lg:hidden">{datasetSelectors}</div>
              {validationButton}
              {queryButton}
              {filterPanel}
            </div>

            <div className="pointer-events-none absolute bottom-10 left-57 right-15 z-10 flex justify-center">
              <div className="pointer-events-auto flex w-full max-w-full flex-col">
                <CoverageHistogramCard
                  countryId={errorModeActive ? null : coverageCountry?.iso3 ?? null}
                  countryName={errorModeActive ? null : coverageCountry?.name ?? null}
                  version={currentVersion}
                  getAccessToken={getAccessToken}
                  rangeStart={debouncedCoverageRangeStart}
                  rangeEnd={debouncedCoverageRangeEnd}
                />
                {dateRange}
              </div>
            </div>
          </>
        )
      })()}

      <MapLegend
        colorScale={COLOR_SCALE}
        maxExamined={maxExamined}
        sizeScale={sizeScale}
        onSizeScaleChange={setSizeScale}
        errorModeActive={errorModeActive}
      />

      {isMobile && (
        <div className="absolute top-4 right-4 z-20 flex w-48 flex-col gap-2">
          <ValidationFailuresButton
            errorModeActive={errorModeActive}
            onToggle={setErrorModeActive}
            variant="toggle"
          />

          {!errorModeActive && coverageCountry != null && (
            <Dialog open={isCoveragePopupOpen} onOpenChange={setIsCoveragePopupOpen}>
              <DialogTrigger
                aria-label="Show PR coverage"
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-border/60 bg-background/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-foreground/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md transition-colors hover:bg-muted/40"
              >
                <BarChart3 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                PR coverage
              </DialogTrigger>
              <DialogContent className="w-[95vw] max-w-none">
                <DialogHeader>
                  <DialogTitle className="text-sm font-semibold uppercase tracking-wider">
                    PR coverage
                    {coverageCountry.name != null && (
                      <span className="ml-1.5 font-medium normal-case tracking-normal text-muted-foreground">
                        {coverageCountry.name}
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <CoverageHistogramPanel
                  countryId={coverageCountry.iso3}
                  countryName={coverageCountry.name}
                  version={currentVersion}
                  getAccessToken={getAccessToken}
                  rangeStart={debouncedCoverageRangeStart}
                  rangeEnd={debouncedCoverageRangeEnd}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      <MapZoomControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
      />

      {queryDialogMounted && (
        <Suspense fallback={null}>
          <PrClusterQueryDialog
            open={queryDialogOpen}
            onOpenChange={setQueryDialogOpen}
            points={points}
          />
        </Suspense>
      )}
    </div>
  )
}
